// Read more / read less for clamped copy (collection intros, product
// descriptions). The button is hidden unless the text is actually cut off, so
// short copy shows in full with nothing to click, and it re-checks when the
// screen size changes.
//
// The copy slides open and shut rather than jumping. Without that, everything
// below it jumped too, and anything positioned against it (the kit steps under
// the case page's switch) had a frame where it sat over the text or left a gap.
// Animating the height gives the page one smooth change to follow instead.
//
// The collection intro and the product description each ask for this file,
// and a page can carry both. It is a module, so it runs once however many ask,
// and each button opens and shuts once per click.

const DURATION = 320;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const setup = (button) => {
  if (button.dataset.readMoreReady) return;
  const text = document.getElementById(button.getAttribute('aria-controls'));
  if (!text) return;

  button.dataset.readMoreReady = 'true';

  // Where the copy is heading, which during an animation is not the same as
  // the class it currently wears: a closing description keeps `is-expanded`
  // until it has finished shrinking, so its paragraphs stay laid out as
  // paragraphs while they slide away.
  let open = false;
  let animation = null;

  const check = () => {
    if (open || text.classList.contains('is-expanded')) return;
    button.hidden = text.scrollHeight <= text.clientHeight + 1;
  };

  /** The copy's height in one state or the other, read without painting it. */
  const heightWhen = (expanded) => {
    const was = text.classList.contains('is-expanded');
    text.classList.toggle('is-expanded', expanded);
    const height = text.getBoundingClientRect().height;
    text.classList.toggle('is-expanded', was);
    return height;
  };

  const settle = () => {
    animation = null;
    text.style.removeProperty('overflow');
    text.classList.toggle('is-expanded', open);
  };

  button.addEventListener('click', () => {
    open = !open;
    button.setAttribute('aria-expanded', String(open));
    // A button with its own label element keeps it, so the label's styling
    // survives; the collection intro's plain button just takes the text.
    const label = button.querySelector('[data-read-more-label]') || button;
    label.textContent = open ? button.dataset.lessLabel : button.dataset.moreLabel;

    // Read before cancelling: a click mid-slide starts from wherever the
    // copy has got to, not from either end.
    const from = text.getBoundingClientRect().height;
    animation?.cancel();
    animation = null;

    const to = heightWhen(open);

    if (reduceMotion.matches || Math.abs(to - from) < 1) {
      settle();
      return;
    }

    // Opening shows the full copy straight away and reveals it; closing
    // keeps it full until it has shrunk to the clamp, then swaps to the
    // clamped copy, whose first lines are the ones already showing.
    text.classList.add('is-expanded');
    text.style.overflow = 'hidden';

    const current = text.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: DURATION,
      easing: EASING,
    });
    animation = current;
    // A cancelled slide rejects; the click that cancelled it has already
    // started the next one, which settles instead.
    current.finished.then(() => animation === current && settle()).catch(() => {});
  });

  check();
  new ResizeObserver(check).observe(text);
};

const setupAll = (root = document) => root.querySelectorAll('[data-read-more]').forEach(setup);

setupAll();
document.addEventListener('shopify:section:load', (event) => setupAll(event.target));
